import type { RouteLocationRaw } from 'vue-router';
import { storeToRefs } from 'pinia';
import { computed } from 'vue';
import { useRoute, useRouter } from '#app/composables/router';
import { isRouteVisible, useEntitlement, useOrganizationEntitlement } from '#layers/access/public';
import { isRouteWithin, unreachable } from '#layers/base/public';
import { resolveSettingsNavigation } from '#layers/settings/public';
import { CONNECT_ROUTE_NAME, DATA_MODEL_ROUTE_NAME, WORKSPACE_SETTINGS_ROUTE_NAME } from '#layers/spaces/public';
import { useActiveCollection } from '#layers/ui-workspace/composables/use-active-collection';
import { toWorkspaceRoute } from '#layers/ui-workspace/utils/workspace-route';
import { useWorkspaceStore } from '#layers/workspace/public';

type SystemSpaceId = typeof CONNECT_ROUTE_NAME | typeof DATA_MODEL_ROUTE_NAME | typeof WORKSPACE_SETTINGS_ROUTE_NAME;

export interface SystemSpace {
	id: SystemSpaceId;
	labelKey: string;
	icon: string;
}

export interface SystemSpaceNavigationItem extends SystemSpace {
	routeName: string;
	routeParams: Record<string, string>;
	to: RouteLocationRaw;
	active: boolean;
}

const SYSTEM_SPACES: SystemSpace[] = [
	{ id: CONNECT_ROUTE_NAME, labelKey: 'pages.connect.title', icon: 'lucide:plug' },
	{ id: DATA_MODEL_ROUTE_NAME, labelKey: 'pages.data_model.title', icon: 'lucide:database' },
	{ id: WORKSPACE_SETTINGS_ROUTE_NAME, labelKey: 'pages.settings.title', icon: 'lucide:settings' },
];

export function useActiveSystemSpace() {
	const route = useRoute();
	return computed(() => SYSTEM_SPACES.find((space) => isRouteWithin(route, space.id)));
}

export function useVisibleSystemSpaces() {
	const router = useRouter();
	const { can } = useEntitlement();
	const { can: canOrganization } = useOrganizationEntitlement();

	const routes = computed(() => router.getRoutes());

	function isSpaceVisible(id: SystemSpaceId): boolean {
		if (id === CONNECT_ROUTE_NAME) return true;
		if (id === DATA_MODEL_ROUTE_NAME) return isRouteVisible(routes.value, DATA_MODEL_ROUTE_NAME, { can, canOrganization });
		if (id === WORKSPACE_SETTINGS_ROUTE_NAME) return resolveSettingsNavigation({ routes: routes.value, can, canOrganization }).groups.length > 0;

		throw unreachable(id);
	}

	return computed(() => SYSTEM_SPACES.filter((space) => isSpaceVisible(space.id)));
}

export function useSystemSpaceNavigation() {
	const route = useRoute();
	const visibleSpaces = useVisibleSystemSpaces();
	const { currentWorkspaceSlug } = storeToRefs(useWorkspaceStore());
	const { dataModelModeRoute } = useActiveCollection();

	function createItem(space: SystemSpace): SystemSpaceNavigationItem {
		const { name: routeName, params: routeParams } = space.id === DATA_MODEL_ROUTE_NAME
			? dataModelModeRoute.value
			: { name: space.id, params: {} };

		return {
			...space,
			routeName,
			routeParams,
			to: toWorkspaceRoute(currentWorkspaceSlug.value, routeName, routeParams),
			active: isRouteWithin(route, space.id),
		};
	}

	const items = computed<SystemSpaceNavigationItem[]>(() => visibleSpaces.value.map((space) => createItem(space)));

	return { items };
}
